/**
 * Parses Word & Brown raw benefits array into structured planBenefits format
 * for storage via POST /api/plan-benefits/bulk.
 */

interface WBBenefit {
  name?: string;
  Name?: string;
  value?: string;
  Value?: string;
  deductibleApply?: string;
  DeductibleApply?: string;
}

interface BenefitFields {
  description: string;
  inNetworkDeductible?: number;
  outNetworkDeductible?: number;
  inNetworkCoinsurance?: number;
  outNetworkCoinsurance?: number;
  inNetworkCopay?: number;
  outNetworkCopay?: number;
  maxOutOfPocket?: number;
  annualMaximum?: number;
  deductibleApply?: boolean;
}

export function parseDollarAmount(value: string): number | undefined {
  const cleaned = value.replace(/[$,\s]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? undefined : num;
}

export function parsePercentage(value: string): number | undefined {
  const cleaned = value.replace(/[%\s]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? undefined : num;
}

function mergeBenefit(
  existing: BenefitFields | undefined,
  patch: Partial<BenefitFields>,
  descriptionPart: string,
): BenefitFields {
  if (!existing) {
    return {
      description: descriptionPart,
      ...patch,
    };
  }
  return {
    ...existing,
    ...Object.fromEntries(
      Object.entries(patch).filter(([, v]) => v !== undefined),
    ),
    description: existing.description
      ? `${existing.description}; ${descriptionPart}`
      : descriptionPart,
  };
}

/**
 * Extract in-network benefits (BenefitSet1) from W&B data.
 * BenefitSet1 = in-network, BenefitSet3 = out-of-network.
 * Mixing both sets causes incorrect values (e.g. out-of-network percentages
 * overwriting in-network copays). Falls back to flat array passthrough.
 */
function flattenBenefitSets(raw: any): WBBenefit[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    // Prefer BenefitSet1 (in-network) if available
    if (Array.isArray(raw.BenefitSet1) && raw.BenefitSet1.length > 0) {
      return raw.BenefitSet1;
    }
    const allSets = Object.values(raw).filter(Array.isArray) as WBBenefit[][];
    return allSets.flat();
  }
  return [];
}

export function parseWBBenefits(
  benefits: WBBenefit[] | Record<string, WBBenefit[]>,
): Record<string, BenefitFields> {
  const flat = flattenBenefitSets(benefits);
  const result: Record<string, BenefitFields> = {};

  for (const benefit of flat) {
    const name = (benefit.name || benefit.Name || "").toLowerCase();
    const value = benefit.value || benefit.Value || "";
    if (!name) continue;

    const descPart = `${benefit.name || benefit.Name}: ${value}`;
    const dollar = parseDollarAmount(value);
    const pct = parsePercentage(value);
    const deductibleApplyRaw = benefit.deductibleApply || benefit.DeductibleApply || "";
    const deductibleApply = deductibleApplyRaw.toUpperCase() === "Y";

    // Medical deductible: "Medical (Individual/Family)" or generic "Deductible" (not drug/rx)
    if (
      (name.includes("deductible") &&
        !name.includes("drug") &&
        !name.includes("rx") &&
        !name.includes("prescription")) ||
      (name.startsWith("medical") && name.includes("individual"))
    ) {
      // W&B uses "N/A" or "None" to mean $0 deductible (no deductible applies)
      const deductibleValue = dollar ?? (
        /^\s*(n\/?a|none|no charge|\$0)\s*$/i.test(value) ? 0 : undefined
      );
      result.medical = mergeBenefit(result.medical, {
        inNetworkDeductible: deductibleValue,
        deductibleApply,
      }, descPart);
    }
    // Office visit / PCP / Primary care visit
    else if (
      name.includes("primary care") ||
      (name.includes("office") && (name.includes("visit") || name.includes("pcp")))
    ) {
      result.medical = mergeBenefit(result.medical, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Coinsurance
    else if (name.includes("coinsurance")) {
      result.medical = mergeBenefit(result.medical, {
        inNetworkCoinsurance: pct,
        deductibleApply,
      }, descPart);
    }
    // Out of pocket max
    else if (
      name.includes("out of pocket") ||
      name.includes("oop") ||
      name.includes("out-of-pocket")
    ) {
      result.medical = mergeBenefit(result.medical, {
        maxOutOfPocket: dollar,
        deductibleApply,
      }, descPart);
    }
    // Emergency / ER (use word boundary to avoid matching "tier", "other", etc.)
    else if (
      name.includes("emergency") ||
      /\ber\b/.test(name) ||
      name.startsWith("er ")
    ) {
      result.emergency = mergeBenefit(result.emergency, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Specialist
    else if (name.includes("specialist")) {
      result.specialist = mergeBenefit(result.specialist, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Urgent care
    else if (name.includes("urgent care") || name.includes("urgent_care")) {
      result.urgent_care = mergeBenefit(result.urgent_care, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Hospital / Inpatient
    else if (name.includes("hospital") || name.includes("inpatient")) {
      result.hospital = mergeBenefit(result.hospital, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Rx generic / tier 1
    else if (
      (name.includes("rx") || name.includes("prescription") || name.includes("drug")) &&
      (name.includes("generic") || name.includes("tier 1") || name.includes("tier1"))
    ) {
      result.prescription = mergeBenefit(result.prescription, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Rx brand / tier 2
    else if (
      (name.includes("rx") || name.includes("prescription") || name.includes("drug")) &&
      (name.includes("brand") || name.includes("tier 2") || name.includes("tier2") || name.includes("preferred"))
    ) {
      result.prescription = mergeBenefit(result.prescription, {
        outNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Mental health
    else if (name.includes("mental health")) {
      result.mental_health = mergeBenefit(result.mental_health, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Preventive
    else if (name.includes("preventive")) {
      result.preventive = mergeBenefit(result.preventive, { deductibleApply }, descPart);
    }
    // Maternity
    else if (name.includes("maternity")) {
      result.maternity = mergeBenefit(result.maternity, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
    // Surgery / Ambulatory Surgery Center
    else if (name.includes("ambulatory") || name.includes("surgery center") || name.includes("outpatient surgery")) {
      result.surgery = mergeBenefit(result.surgery, {
        inNetworkCopay: dollar,
        deductibleApply,
      }, descPart);
    }
  }

  return result;
}
